import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { Option } from "@/common/types/option";
import { ControlledByType, GovernmentAllocationAllowedCategoryAndProductData } from "@/features/privateCompanies/types/companyGovernmentAllocation.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import ProductCategoryDistributionLimitForm from "./ProductCategoryDistributionLimitForm";
import { ProductCategoryDistributionLimitValidationSchema, productCategoryDistributionsLimitControlledByCurrencySchema, productCategoryDistributionsLimitControlledByQuantitySchema } from "@/features/privateCompanies/schemas/governmentAllocations/distributionsLimits.schema";
import { useCreateGovernmentAllocationDistributionLimit } from "@/features/privateCompanies/hooks/governmentAllocations/distributionsLimits/useCreateGovernmentAllocationDistributionLimit";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";

interface CreateDistributionLimitProps {
  productCategory: (GovernmentAllocationAllowedCategoryAndProductData & Option) | null;
  onDismiss: () => void;
  governmentAllocationId?: string;
  controlledBy?: ControlledByType;
}

const CreateDistributionLimit = ({
  productCategory,
  onDismiss,
  governmentAllocationId,
  controlledBy,
}: CreateDistributionLimitProps) => {
  const isQuantity = controlledBy === 'quantity';

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    mutate: createGovernmentAllocationDistributionLimit,
    isPending: createGovernmentAllocationDistributionLimitIsPending,
  } = useCreateGovernmentAllocationDistributionLimit({
    governmentAllocationId,
    productCategoryId: productCategory?.value,
    controlledBy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsDistributionsLimits', governmentAllocationId] });
      queryClient.invalidateQueries({ queryKey: ['governmentAllocation', governmentAllocationId] });

      onDismiss();

      setTimeout(reset, 500);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    }
  })

  const formMethods = useForm<ProductCategoryDistributionLimitValidationSchema>({
    resolver: zodResolver(
      isQuantity
        ? productCategoryDistributionsLimitControlledByQuantitySchema
        : productCategoryDistributionsLimitControlledByCurrencySchema
    ),
    defaultValues: {
      maximum_value: '',
      maximum_quantity: '',
      product_distribution_limits_attributes: [],
    },
  });

  const {
    handleSubmit,
    reset,
    setError,
  } = formMethods;

  const onSubmit: SubmitHandler<ProductCategoryDistributionLimitValidationSchema> = (data) => createGovernmentAllocationDistributionLimit(data);

  return (
    <>
      <Modal
        isOpen={!!productCategory}
        setIsOpen={(value) => {
          if (!value) {
            onDismiss();

            setTimeout(reset, 500);
          }
        }}
        title="Limite de distribuição do empenho"
        showClose
        className="min-w-[640px] overflow-hidden"
        disabled={createGovernmentAllocationDistributionLimitIsPending}
        body={(
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-6 text-white-800">
              <ProductCategoryDistributionLimitForm
                governmentAllocationId={governmentAllocationId}
                productCategoryId={productCategory?.value}
                controlledBy={controlledBy}
              />

              <Button
                onClick={handleSubmit(onSubmit)}
                isLoading={createGovernmentAllocationDistributionLimitIsPending}
              >
                Salvar
              </Button>
            </div>
          </FormProvider>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default CreateDistributionLimit;
